import { useState, useEffect, useRef, useMemo } from "react"
import { pdf } from "@react-pdf/renderer"
import { PDFDocument } from "pdf-lib"
import {
  CoversheetDocument,
  type CoversheetData,
} from "@/components/coversheet-document"
import { LoaderIcon, AlertCircleIcon } from "lucide-react"

/**
 * Generates a merged PDF preview that combines:
 * 1. The generated coversheet (if provided)
 * 2. All attached PDF files
 *
 * Non-PDF attachments are skipped in the visual preview but still listed.
 * The result is displayed in an iframe.
 */

type FaxPreviewProps = {
  /** Coversheet data for the current recipient, or null if no coversheet */
  coversheetData: CoversheetData | null
  /** An already-uploaded coversheet PDF file, used instead of generated one */
  coversheetFile?: File | null
  /** Attached document files */
  files: File[]
}

export function FaxPreview({
  coversheetData,
  coversheetFile,
  files,
}: FaxPreviewProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [skippedFiles, setSkippedFiles] = useState<string[]>([])
  const prevUrlRef = useRef<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced data to avoid excessive regeneration while typing
  const [debouncedData, setDebouncedData] = useState(coversheetData)
  const [debouncedCoversheetFile, setDebouncedCoversheetFile] =
    useState(coversheetFile)
  const [debouncedFiles, setDebouncedFiles] = useState(files)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedData(coversheetData)
      setDebouncedCoversheetFile(coversheetFile)
      setDebouncedFiles(files)
    }, 500)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [coversheetData, coversheetFile, files])

  // Memoize the document element for react-pdf
  const coversheetDoc = useMemo(() => {
    if (!debouncedData) return null
    return <CoversheetDocument data={debouncedData} />
  }, [debouncedData])

  useEffect(() => {
    let cancelled = false

    async function generateMergedPdf() {
      setLoading(true)
      setError(null)
      setSkippedFiles([])

      try {
        const mergedPdf = await PDFDocument.create()
        const skipped: string[] = []

        // 1. Add coversheet
        if (debouncedCoversheetFile) {
          // Use uploaded coversheet file
          try {
            const bytes = await debouncedCoversheetFile.arrayBuffer()
            const coversheetPdf = await PDFDocument.load(bytes)
            const pages = await mergedPdf.copyPages(
              coversheetPdf,
              coversheetPdf.getPageIndices()
            )
            for (const page of pages) mergedPdf.addPage(page)
          } catch {
            skipped.push(debouncedCoversheetFile.name + " (invalid PDF)")
          }
        } else if (coversheetDoc) {
          // Generate coversheet from react-pdf
          try {
            const blob = await pdf(coversheetDoc).toBlob()
            const bytes = await blob.arrayBuffer()
            const coversheetPdf = await PDFDocument.load(bytes)
            const pages = await mergedPdf.copyPages(
              coversheetPdf,
              coversheetPdf.getPageIndices()
            )
            for (const page of pages) mergedPdf.addPage(page)
          } catch {
            // If coversheet generation fails, continue without it
          }
        }

        // 2. Add attached files (only PDFs can be merged)
        for (const file of debouncedFiles) {
          if (
            file.type === "application/pdf" ||
            file.name.toLowerCase().endsWith(".pdf")
          ) {
            try {
              const bytes = await file.arrayBuffer()
              const attachedPdf = await PDFDocument.load(bytes, {
                ignoreEncryption: true,
              })
              const pages = await mergedPdf.copyPages(
                attachedPdf,
                attachedPdf.getPageIndices()
              )
              for (const page of pages) mergedPdf.addPage(page)
            } catch {
              skipped.push(file.name)
            }
          } else {
            // Non-PDF files can't be visually merged
            skipped.push(file.name + " (not a PDF)")
          }
        }

        if (cancelled) return

        setSkippedFiles(skipped)

        // If we have no pages at all, show a message
        if (mergedPdf.getPageCount() === 0) {
          setError("No previewable PDF content found.")
          setLoading(false)
          return
        }

        const mergedBytes = await mergedPdf.save()
        const blob = new Blob([mergedBytes], { type: "application/pdf" })
        const url = URL.createObjectURL(blob)

        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
        prevUrlRef.current = url

        if (!cancelled) {
          setPdfUrl(url)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to generate preview."
          )
          setLoading(false)
        }
      }
    }

    generateMergedPdf()

    return () => {
      cancelled = true
    }
  }, [debouncedData, debouncedCoversheetFile, debouncedFiles, coversheetDoc])

  // Clean up URL on unmount
  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
    }
  }, [])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-3">
        <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          Generating fax preview...
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-3 p-4 text-center">
        <AlertCircleIcon className="size-5 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {skippedFiles.length > 0 && (
        <div className="px-3 py-2 border-b bg-muted/30 text-xs text-muted-foreground">
          <span className="font-medium">
            {skippedFiles.length} file{skippedFiles.length !== 1 ? "s" : ""}{" "}
            not included in preview:
          </span>{" "}
          {skippedFiles.join(", ")}
        </div>
      )}
      {pdfUrl && (
        <iframe
          src={pdfUrl}
          className="w-full flex-1 min-h-[400px]"
          title="Full fax preview"
        />
      )}
    </div>
  )
}
